import axios from 'axios';
import { 
  API_BASE_URL, 
  getTokenFromStorage,
  STORAGE_KEYS
} from '../constant/constant';

// Create axios instance with default config
const createAdminDashboardApi = () => {
  const adminDashboardApi = axios.create({
    baseURL: `${API_BASE_URL}/admin/dashboard`,
  });

  // Request interceptor to add auth token
  adminDashboardApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  adminDashboardApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, clear Redux Persist storage and redirect to login
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return adminDashboardApi;
};

const adminDashboardApi = createAdminDashboardApi();

// Helper function to handle API errors
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.msg || error.message || defaultMessage;
  throw new Error(errorMessage);
};

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  
  return queryParams.toString();
};

// Admin Dashboard API Service Functions

/**
 * Get main dashboard metrics (KPIs)
 * @returns {Promise<Object>} Main metrics data
 */
export const getMainMetrics = async () => {
  try {
    const response = await adminDashboardApi.get('/main-metrics');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch main dashboard metrics');
  }
};

/**
 * Get recent activity data
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of activities to return (default: 5)
 * @returns {Promise<Object>} Recent activity data
 */
export const getRecentActivity = async (params = {}) => {
  try {
    const { limit = 5 } = params;
    const queryParams = buildQueryParams({ limit });
    
    const response = await adminDashboardApi.get(`/recent-activity?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch recent activity');
  }
};

/**
 * Get quick actions data
 * @returns {Promise<Object>} Quick actions data
 */
export const getQuickActions = async () => {
  try {
    const response = await adminDashboardApi.get('/quick-actions');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch quick actions');
  }
};

/**
 * Search functionality across users, support tickets, and payments
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query (required, minimum 2 characters)
 * @param {string} params.type - Search type: 'all', 'users', 'tickets', 'payments' (default: 'all')
 * @param {number} params.limit - Maximum number of results (default: 10)
 * @returns {Promise<Object>} Search results
 */
export const searchDashboard = async (params) => {
  try {
    const { q, type = 'all', limit = 10 } = params;
    
    if (!q || q.length < 2) {
      throw new Error('Search query must be at least 2 characters long');
    }
    
    const queryParams = buildQueryParams({ q, type, limit });
    
    const response = await adminDashboardApi.get(`/search?${queryParams}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to perform search');
  }
};

/**
 * Get all dashboard data in a single request for optimal performance
 * @returns {Promise<Object>} Combined dashboard data
 */
export const getCombinedDashboardData = async () => {
  try {
    const response = await adminDashboardApi.get('/combined');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch combined dashboard data');
  }
};

// Export all functions as adminDashboardService object
export const adminDashboardService = {
  getMainMetrics,
  getRecentActivity,
  getQuickActions,
  searchDashboard,
  getCombinedDashboardData
};

export default adminDashboardService;
